<script setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useNavigationStore } from '../stores/NavigationStore'

const props = defineProps({
  latestLibraryActivities: {
    type: Array,
    default: () => []
  },
  /** Rows from the agency-lesson-activity endpoint (already resolved and labelled server-side). */
  latestTransformActivities: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  /**
   * Each tab — and the table behind it — renders only for a user who holds the matching
   * module permission. A transform-only user must never see the Content Library tab, its
   * table, the "Go to Content Library" link, or the per-row deep links into it.
   */
  hasContentLibrary: {
    type: Boolean,
    default: true
  },
  hasTransform: {
    type: Boolean,
    default: false
  }
})

const navigationStore = useNavigationStore()
const { customNavigation } = storeToRefs(navigationStore)

const TAB = {
  CONTENT_LIBRARY: 'contentLibrary',
  TRANSFORM: 'transform'
}

const tabs = computed(() => [
  ...(props.hasContentLibrary ? [{ id: TAB.CONTENT_LIBRARY, label: 'Content Library' }] : []),
  ...(props.hasTransform ? [{ id: TAB.TRANSFORM, label: 'BCS Transform' }] : [])
])

const selectedTab = ref(null)

/**
 * Falls back to the first permitted tab, so the card is never left showing a pane the user
 * has no access to — including when permissions resolve after the first render.
 */
const activeTab = computed(() => {
  const available = tabs.value.map((tab) => tab.id)
  if (selectedTab.value && available.includes(selectedTab.value)) {
    return selectedTab.value
  }
  return available[0] ?? null
})

const isContentLibraryTab = computed(() => activeTab.value === TAB.CONTENT_LIBRARY)
const isTransformTab = computed(() => activeTab.value === TAB.TRANSFORM)

const formatActivityDate = (timestamp) => {
  if (!timestamp) return 'N/a'
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  })
}

const goToContentLibrary = () => {
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.contentLibrary.name
  })
}

/*
 * Opens the lesson behind an activity row, from either the name or the row button.
 * Navigates straight to the route rather than through useTransformLessonCard, which resets a
 * passed or failed quiz on the way in — clearing someone's attempt from a read-only activity
 * feed would be a surprise. moduleId/moduleTitle come from the endpoint because the lesson
 * detail needs them to rebuild its breadcrumb.
 */
const goToLesson = (activity) => {
  if (!activity?.lessonId) return
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.transformLessonDetail.name,
    params: {
      lessonId: activity.lessonId,
      lessonTitle: activity.lessonTitle ?? '',
      moduleId: activity.moduleId ?? undefined,
      moduleTitle: activity.moduleTitle ?? ''
    }
  })
}

/*
 * A certification row names the course, not a lesson, so it has no lesson detail to open:
 * it goes to the Transform dashboard, the course's own landing page.
 */
const goToTransformActivity = (activity) => {
  if (activity?.lessonId) {
    goToLesson(activity)
    return
  }
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.transformLearningDashboard.name
  })
}

const goToLibraryItem = (activity) => {
  if (!props.hasContentLibrary) return
  customNavigation.value.navigate({
    newPage: customNavigation.value.routes.bcsContentLibraryTaxonomyItems.name,
    query: {
      taxonomyGroupId: activity.taxonomyGroupId,
      itemId: activity.itemId
    }
  })
}
</script>

<template>
  <div class="row d-flex justify-content-center mt-0 ps-1.5 pe-2.5">
    <div class="col-md-12 pe-0 ps-2">
      <div class="card">
        <div class="card-header card-header-activity bg-light ps-4 pe-4 d-flex justify-content-between align-items-center">
          <h5 class="card-title add-teamates-link header-22 text-secondary mb-0">
            <strong>Latest Activity</strong>
          </h5>
          <a
            v-if="hasContentLibrary && isContentLibraryTab"
            class="view-full-profile-link hand-cursor text-decoration-none text-14 text-primary"
            @click.prevent="goToContentLibrary"
            href="#">
            <strong>Go to Content Library</strong>
            <i class="bi bi-chevron-right ms-2"></i>
          </a>
        </div>

        <!-- With a single module there is nothing to switch between, so the strip is noise:
             the pane below still renders on its own. -->
        <ul
          v-if="tabs.length > 1"
          id="latestActivityTabs"
          class="nav nav-tabs latest-activity-tabs px-4"
          role="tablist">
          <li v-for="tab in tabs" :key="tab.id" class="nav-item" role="presentation">
            <button
              class="nav-link"
              :class="{ active: activeTab === tab.id }"
              :id="`latest-activity-tab-${tab.id}`"
              @click="selectedTab = tab.id"
              type="button"
              role="tab"
              :aria-controls="`latest-activity-pane-${tab.id}`"
              :aria-selected="activeTab === tab.id">
              {{ tab.label }}
            </button>
          </li>
        </ul>

        <div class="card-body pt-0 px-0 pb-0 latest-activity-panes" :class="{ 'has-tab-strip': tabs.length > 1 }">
          <!-- Content Library tab — unchanged behaviour. -->
          <div
            v-if="hasContentLibrary"
            :id="`latest-activity-pane-${TAB.CONTENT_LIBRARY}`"
            role="tabpanel"
            class="table-responsive latest-activity-pane"
            :class="{ 'is-hidden': !isContentLibraryTab }"
            :aria-hidden="!isContentLibraryTab">
            <table class="table latest-activity-table mb-1 border-0" v-if="latestLibraryActivities.length > 0">
              <thead>
                <tr>
                  <th scope="col" class="text-16 ps-4">
                    <strong>File Name</strong>
                  </th>
                  <th scope="col" class="text-16 text-start ">
                    <strong>Latest Activity</strong>
                  </th>
                  <th scope="col" class="text-16 text-start">
                    <strong>Activity Date</strong>
                  </th>
                  <th scope="col" class="text-16 text-start">
                    <strong>Team Member</strong>
                  </th>
                  <th scope="col" class="text-16 text-start"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="activity in latestLibraryActivities" :key="`${activity.userId}-${activity.timestamp}`">
                  <td class="border border-0 ps-4 text-truncate latest-library-activity-file-cell">
                    <a
                      v-if="hasContentLibrary"
                      @click.prevent="goToLibraryItem(activity)"
                      href="#"
                      class="text-primary text-decoration-none text-14 hand-cursor latest-library-activity-file-link">
                      <strong>
                        {{ activity.fileName || 'N/a' }}
                      </strong>
                    </a>
                    <span v-else class="text-14 latest-library-activity-file-link">
                      <strong>
                        {{ activity.fileName || 'N/a' }}
                      </strong>
                    </span>
                  </td>
                  <td class="border border-0 py-0 text-truncate">
                    <span class="d-inline-block latest-activity-pill duedate-pill rounded-pill bg-light px-2 text-14 text-secondary text-decoration-none">
                      <strong>
                        {{ activity.activityDescription || 'N/a' }}
                      </strong>
                    </span>
                  </td>
                  <td class="border border-0 text-14 text-secondary py-0 text-start">
                    {{ formatActivityDate(activity.timestamp) }}
                  </td>
                  <td class="border border-0 p-2 text-truncate text-start">
                    <span class="latest-activity-member">
                      {{ activity.teamMemberName || 'N/a' }}
                    </span>
                  </td>
                  <td class="border border-0 py-0 pe-4 text-end">
                    <button
                      v-if="hasContentLibrary"
                      @click="goToLibraryItem(activity)"
                      class="hand-cursor btn btn-primary px-2 py-0.5 gotoEmployerBtn">
                      <i class="bi bi-chevron-right text-white"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <table class="table mb-1" style="min-height: 160px" v-else>
              <tbody>
                <tr v-if="!loading">
                  <td colspan="5" class="text-center text-14 text-secondary py-4">
                    Your agency’s latest content library activity will appear here.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- BCS Transform tab. Rows are resolved and labelled by the gateway; this only
               renders them, so the status vocabulary lives in one place on the server. -->
          <div
            v-if="hasTransform"
            :id="`latest-activity-pane-${TAB.TRANSFORM}`"
            role="tabpanel"
            class="table-responsive latest-activity-pane"
            :class="{ 'is-hidden': !isTransformTab }"
            :aria-hidden="!isTransformTab">
            <table class="table latest-activity-table mb-1 border-0" v-if="latestTransformActivities.length > 0">
              <thead>
                <tr>
                  <th scope="col" class="text-16 ps-4">
                    <strong>Course, Lesson or Quiz Name</strong>
                  </th>
                  <th scope="col" class="text-16 text-start">
                    <strong>Latest Activity</strong>
                  </th>
                  <th scope="col" class="text-16 text-start">
                    <strong>Activity Date</strong>
                  </th>
                  <th scope="col" class="text-16 text-start">
                    <strong>Team Member</strong>
                  </th>
                  <th scope="col" class="text-16 text-start"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="activity in latestTransformActivities"
                  :key="activity.id || `${activity.lessonId}-${activity.teamMemberName}-${activity.activityDate}`">
                  <td class="border border-0 ps-4 text-truncate latest-library-activity-file-cell">
                    <a
                      @click.prevent="goToTransformActivity(activity)"
                      href="#"
                      class="text-primary text-decoration-none text-14 hand-cursor latest-library-activity-file-link">
                      <strong>{{ activity.lessonTitle || 'N/a' }}</strong>
                    </a>
                  </td>
                  <td class="border border-0 py-0 text-truncate">
                    <span class="d-inline-block latest-activity-pill duedate-pill rounded-pill bg-light px-2 text-14 text-secondary text-decoration-none">
                      <strong>{{ activity.statusLabel || 'N/a' }}</strong>
                    </span>
                  </td>
                  <td class="border border-0 text-14 text-secondary py-0 text-start">
                    {{ formatActivityDate(activity.activityDate) }}
                  </td>
                  <td class="border border-0 p-2 text-truncate text-start">
                    <span class="latest-activity-member">{{ activity.teamMemberName || 'N/a' }}</span>
                  </td>
                  <td class="border border-0 py-0 pe-4 text-end">
                    <button
                      @click="goToTransformActivity(activity)"
                      class="hand-cursor btn btn-primary px-2 py-0.5 gotoEmployerBtn">
                      <i class="bi bi-chevron-right text-white"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <!-- No rows: render a plain block rather than an empty table. Bootstrap draws a
                 bottom border on every table cell, which showed up as a stray rule under the
                 message. The wrapper keeps the tab's height so switching tabs does not make
                 the card jump. -->
            <div v-else class="latest-activity-empty text-center text-14 text-secondary">
              <span v-if="!loading">Your agency’s latest Transform activity will appear here.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card-header-activity {
  height: 51px;
}

tr {
  border-bottom: 1px solid #c5cdd4;
}

tbody tr:last-child {
  border: none;
}

.latest-library-activity-file-cell {
  color: var(--bs-primary);
}

.latest-library-activity-file-link {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit !important;
}

/*
 * Fixed column geometry, shared by both tabs.
 *
 * The two panes are stacked in one grid cell, so both tables have to resolve to the very
 * same column widths — with the default auto layout each tab sizes its columns from its own
 * content (file names vs. lesson titles, plus the much wider "Course, Lesson or Quiz Name"
 * header), so every column jumped on switching tabs. `table-layout: fixed` makes the widths
 * below authoritative and content-independent, and it is one declaration for both tables so
 * the two tabs cannot drift apart. Overflow is ellipsised by the cells' .text-truncate.
 *
 * Only the first row of a fixed-layout table sets the widths, hence `thead th`:
 *   1 name · 2 latest activity · 3 activity date · 4 team member · 5 row button
 */
.latest-activity-table {
  table-layout: fixed;
}

.latest-activity-table thead th:nth-child(1) { width: 34%; }
.latest-activity-table thead th:nth-child(2) { width: 21%; }
.latest-activity-table thead th:nth-child(3) { width: 15%; }
.latest-activity-table thead th:nth-child(4) { width: 22%; }
.latest-activity-table thead th:nth-child(5) { width: 8%; }

/*
 * Status pill: bounded by its cell so a long label ellipsises inside the pill, instead of
 * the cell's own overflow cutting the pill's rounded background in half.
 */
.latest-activity-pill {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/*
 * Folder tabs: each tab is a bordered shape with rounded top corners that sits on the
 * table's top line. The `-1px` bottom margin pulls every tab down so its own bottom border
 * overlaps that line — inactive tabs keep theirs (they read as separate tabs resting on the
 * line), while the active tab hides it, so its body flows into the table below.
 */
/*
 * Both panes occupy the same grid cell, so the card is always as tall as the taller of the
 * two and switching tabs never makes it shrink. The inactive one is hidden with visibility
 * rather than display, which is what keeps it contributing its height to the cell.
 */
.latest-activity-panes {
  display: grid;
}

.latest-activity-pane {
  grid-area: 1 / 1;
}

.latest-activity-pane.is-hidden {
  visibility: hidden;
  pointer-events: none;
}

/*
 * Empty state: same footprint as a populated table, without any table chrome. It fills the
 * whole pane rather than just its own 160px, so when the other tab makes the grid cell
 * taller the message stays centred in the card instead of clinging to the top.
 */
.latest-activity-empty {
  min-height: 160px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
}

.latest-activity-tabs {
  --tab-border: #c5cdd4;
  border-bottom: 1px solid var(--tab-border);
  /* Breathing room between the card header and the tab strip. */
  padding-top: 30px;
  /* Tabs sit flush against each other: the seam between them is their own borders meeting,
     together with the rounded top corners. No gap. */
  gap: 0;
  flex-wrap: nowrap;
}

.latest-activity-tabs .nav-item {
  margin-bottom: -1px;
}

/*
 * 27px between the tab strip's bottom border and the column-header text — the card body has
 * no top padding, so this gap is owned entirely by the header cells. Without a strip the
 * headers sit directly under the card title's own border, where that gap is not wanted, so
 * the cells keep their default padding there.
 */
.card-body.has-tab-strip thead th {
  padding-top: 27px;
}

/* Team Member values — Text 14 Regular, body colour. Shared by both tabs. */
.latest-activity-member {
  color: var(--bs-body-color, #212529);
  font-family: var(--bs-font-sans-serif), 'DM Sans', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: -0.28px;
}

/* Text - 16 - Regular, shared by both tab states; only the colour differs. */
.latest-activity-tabs .nav-link {
  border: 1px solid var(--tab-border);
  border-radius: 6px 6px 0 0;
  background-color: #fff;
  font-family: var(--bs-font-sans-serif), 'DM Sans', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  /* Inactive tabs read as the actionable ones — Eggplant / Primary. */
  color: var(--bs-purple-intense, #5c0cb8);
  /* Width follows the label; the horizontal padding stays symmetrical. */
  padding: 8px 20px;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

/*
 * Hover is completely inert. Bootstrap's .nav-tabs styles change border-color as well as
 * the text colour on hover, so every one of those properties is pinned back to the resting
 * value — otherwise the tab outline flickers on mouse-over.
 */
/*
 * The strip's own border-bottom is drawn one row above the tab's border-bottom (the
 * nav-item's -1px margin), so an inactive tab's white background hides that line while its
 * real border sits on a row the card body paints over. An inset shadow puts the line back on
 * the row that is actually visible, keeping inactive tabs resting on the table's top edge.
 * The active tab has no shadow, so its background merges into the table below.
 */
.latest-activity-tabs .nav-link:not(.active) {
  box-shadow: inset 0 -1px 0 var(--tab-border);
}

.latest-activity-tabs .nav-link:hover,
.latest-activity-tabs .nav-link:focus {
  background-color: #fff;
  color: var(--bs-purple-intense, #5c0cb8);
  border-color: var(--tab-border);
  isolation: auto;
}

.latest-activity-tabs .nav-link.active,
.latest-activity-tabs .nav-link.active:hover,
.latest-activity-tabs .nav-link.active:focus {
  /* Same background as an inactive tab: the selected state is conveyed by the body-colour
     text plus the missing bottom border, which merges the tab into the table below. */
  background-color: #fff;
  color: var(--bs-body-color, #212529);
  border-color: var(--tab-border);
  /*
   * Painted in the background colour rather than transparent: the strip draws its own
   * border-bottom across the full width, so a transparent edge would let that line show
   * through and the active tab would look identical to an inactive one. Sitting 1px lower
   * (the nav-item's negative margin), this white edge covers the line and merges the tab
   * into the table. Inactive tabs keep their own visible bottom border.
   */
  border-bottom-color: #fff;
}
</style>
